// src/app/schedule.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { View, Text, ScrollView, StyleSheet, TouchableOpacity, TextInput } from 'react-native';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import {
  format,
  startOfWeek,
  addDays,
  addWeeks,
  subWeeks,
  subDays,
} from 'date-fns';
import {
  IoChevronBack,
  IoChevronForward,
  IoCalendar,
  IoTime,
  IoNavigate,
  IoAdd,
  IoBriefcase,
  IoDocumentText,
} from 'react-icons/io5';
import { MdAdd, MdRemove, MdEmail } from 'react-icons/md';
import API_BASE_URL from '../apiConfig.js';

// --- helpers ---
const getToken = () => {
  try {
    return localStorage.getItem('userToken');
  } catch {
    return null;
  }
};

// Mobile detection utility
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkIsMobile = () => {
      // Check for mobile device using multiple methods
      const userAgent = navigator.userAgent || navigator.vendor || window.opera;
      const isMobileUA = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent);
      const isSmallScreen = window.innerWidth <= 768;
      const hasTouchSupport = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
      
      // Consider it mobile if it matches mobile user agent OR has small screen with touch support
      setIsMobile(isMobileUA || (isSmallScreen && hasTouchSupport));
    };

    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);
    
    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);

  return isMobile;
};

// Convert Date to "YYYY-MM-DDTHH:mm" for web datetime-local input
const toLocalDatetimeInput = (d) => {
  if (!(d instanceof Date) || isNaN(d)) return '';
  const pad = (n) => String(n).padStart(2, '0');
  const yyyy = d.getFullYear();
  const mm = pad(d.getMonth() + 1);
  const dd = pad(d.getDate());
  const hh = pad(d.getHours());
  const mi = pad(d.getMinutes());
  return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
};

export default function Schedule() {
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();

  // ---------- LEFT: Weekly schedule state ----------
  const [currentWeekStart, setCurrentWeekStart] = useState(
    startOfWeek(new Date(), { weekStartsOn: 0 })
  );
  const [cachedByDate, setCachedByDate] = useState({}); // { 'YYYY-MM-DD': Job[] } keyed by UTC selectedDate
  const [fetchedRanges, setFetchedRanges] = useState([]); // [{start,end}]
  const [loadingJobs, setLoadingJobs] = useState(false);
  const [userPermissions, setUserPermissions] = useState(null);
  const [customersForEmails, setCustomersForEmails] = useState([]); // Separate state for email lookups

  // Modal states (copied from jobs.jsx)
  const [selectedJob, setSelectedJob] = useState(null);
  const [completionOpen, setCompletionOpen] = useState(false);
  const [invoiceOpen, setInvoiceOpen] = useState(false);
  const [quoteOpen, setQuoteOpen] = useState(false);

  // Load user permissions on component mount and check access
  useEffect(() => {
    const loadUserPermissions = async () => {
      try {
        const permissions = localStorage.getItem('userPermissions');
        if (permissions) {
          const parsedPermissions = JSON.parse(permissions);
          setUserPermissions(parsedPermissions);

          const user_type = parsedPermissions?.user_type;
          const jobPerm = parsedPermissions?.job_perm;

          // Block access if user is NOT (Owner OR Admin) AND has no invoice permission
          if ((user_type !== 'owner' && user_type !== 'admin') && jobPerm === 'none') {
            alert("You don't have permission to view jobs. Please contact your account administrator.");
            navigate('/'); // Redirect to home page
            return;
          }
        }
      } catch (error) {
        console.error('Error loading user permissions:', error);
      }
    };
    loadUserPermissions();
  }, [navigate]);

  // Check if user has job write permissions
  const hasJobWritePermission = () => {
    return userPermissions?.job_perm === 'write';
  };

  // Check if user can see prices
  const canSeePrices = () => {
    return userPermissions?.price_perm === 'visible';
  };

  // Check if user can complete/undo a job
  const canCompleteUndoJob = (job) => {
    if (!userPermissions) return false;
    
    // Owner and admin can always complete/undo
    if (userPermissions.user_type === 'owner' || userPermissions.user_type === 'admin') {
      return true;
    }
    
    // If user has read-only job permissions, they cannot complete/undo
    if (userPermissions.job_perm === 'read') {
      return false;
    }
    
    // Check if user is assigned to this job
    const isAssignedToJob = job.assigned_type === 'all' || 
      (job.assigned_type === 'specific' && job.assignedUserIds?.includes(userPermissions.userId));
    
    // If assigned to job and has write permission, allow
    if (isAssignedToJob && userPermissions.job_perm === 'write') {
      return true;
    }
    
    // If NOT assigned but has edit_all_jobs permission and write access, allow
    if (!isAssignedToJob && userPermissions.edit_all_jobs === 1 && userPermissions.job_perm === 'write') {
      return true;
    }
    
    // Otherwise, disable
    return false;
  };

  const weekDays = useMemo(
    () => Array.from({ length: 7 }).map((_, i) => addDays(currentWeekStart, i)),
    [currentWeekStart]
  );
  const weekStartFormatted = format(currentWeekStart, 'MM/dd/yy');
  const weekEndFormatted = format(addDays(currentWeekStart, 6), 'MM/dd/yy');

  // Accept array or {jobs:[...]} or anything array-like
  const coerceJobs = (data) =>
    Array.isArray(data) ? data : (data?.jobs ?? data ?? []);

  const fetchJobsForRange = useCallback(async (startDateStr, endDateStr) => {
    try {
      setLoadingJobs(true);
      const token = getToken();
      const res = await axios.get(
        `${API_BASE_URL}/api/jobs/date-range/${startDateStr}/${endDateStr}`,
        { headers: token ? { Authorization: `Bearer ${token}` } : undefined }
      );
      const list = coerceJobs(res.data);

      setCachedByDate((prev) => {
        const next = { ...prev };
        for (const job of list) {
          const key = job?.selectedDate;
          if (!key) continue;
          if (!next[key]) next[key] = [];
          if (!next[key].some((j) => j.id === job.id)) next[key].push(job);
        }
        return next;
      });
    } catch (e) {
      console.error('Error fetching jobs for range', e);
      window.alert('Failed to load jobs. Please try again later.');
    } finally {
      setLoadingJobs(false);
    }
  }, []);

  // initial prefetch: ~2 weeks before to ~3 weeks after
  useEffect(() => {
    const startStr = format(subDays(currentWeekStart, 15), 'yyyy-MM-dd');
    const endStr = format(addDays(currentWeekStart, 21), 'yyyy-MM-dd');
    fetchJobsForRange(startStr, endStr);
    setFetchedRanges((r) => [...r, { start: startStr, end: endStr }]);
    loadCustomersForEmails(); // Load customers for email lookups
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load customers to get email addresses
  const loadCustomersForEmails = async () => {
    try {
      const token = getToken();
      const res = await axios.get(`${API_BASE_URL}/api/customers`, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
      const list = coerceJobs(res.data);
      // Store in a separate state to avoid conflicts with the form's customer state
      const customerList = Array.isArray(list) ? list : [];
      setCustomersForEmails(customerList);
    } catch (err) {
      console.error('Error loading customers for emails:', err);
    }
  };

  // Helper function to get customer email
  const getCustomerEmail = (customerName) => {
    const customer = customersForEmails.find(c => c?.name === customerName);
    return customer?.email || null;
  };

  // Email customer function
  const emailCustomer = (email) => {
    if (email) {
      window.open(`mailto:${email}`, '_self');
    }
  };

  const isWeekCached = (weekStart) => {
    const weekKey = format(weekStart, 'yyyy-MM-dd');
    return fetchedRanges.some((r) => weekKey >= r.start && weekKey <= r.end);
  };

  const loadWeekIfNeeded = async (weekStart, direction) => {
    if (isWeekCached(weekStart)) return;
    const startOfRange =
      direction === 'forward'
        ? format(subDays(weekStart, 8), 'yyyy-MM-dd')
        : format(subDays(weekStart, 28), 'yyyy-MM-dd');
    const endOfRange =
      direction === 'forward'
        ? format(addDays(weekStart, 27), 'yyyy-MM-dd')
        : format(addDays(weekStart, 8), 'yyyy-MM-dd');

    await fetchJobsForRange(startOfRange, endOfRange);
    setFetchedRanges((r) => [...r, { start: startOfRange, end: endOfRange }]);
  };

  const handleWeekChange = async (direction) => {
    const next =
      direction === 'backward'
        ? subWeeks(currentWeekStart, 1)
        : addWeeks(currentWeekStart, 1);
    setCurrentWeekStart(next);
    await loadWeekIfNeeded(next, direction);
  };

  const formatTimeLocal = (selectedDate, selectedTime) => {
    if (!selectedDate || !selectedTime) return 'Invalid';
    const d = new Date(`${selectedDate}T${selectedTime}Z`);
    return d.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const adjustToLocalYmd = (selectedDate, selectedTime = '00:00:00') => {
    const utc = new Date(`${selectedDate}T${selectedTime}Z`);
    const local = new Date(utc.getTime() - utc.getTimezoneOffset() * 60000);
    return local.toISOString().split('T')[0];
  };

  // Group per job (fixes cross-midnight/time zone issues)
  const jobsForDay = (dayDate) => {
    const targetYmd = format(dayDate, 'yyyy-MM-dd');
    const collected = [];

    for (const [, jobs] of Object.entries(cachedByDate)) {
      for (const job of jobs) {
        const localYmd = adjustToLocalYmd(job.selectedDate, job.selectedTime || '00:00:00');
        if (localYmd === targetYmd) collected.push(job);
      }
    }

    collected.sort((a, b) => {
      const timeA = new Date(`${a.selectedDate}T${a.selectedTime}Z`).getTime();
      const timeB = new Date(`${b.selectedDate}T${b.selectedTime}Z`).getTime();
      return timeA - timeB;
    });
    return collected;
  };

  const handleCompletePress = (job) => {
    setSelectedJob(job);
    setCompletionOpen(true);
  };

  const handleCompletionConfirm = async () => {
    if (!selectedJob) return;
    try {
      const token = getToken();
      await axios.put(
        `${API_BASE_URL}/api/jobs/edit/${selectedJob.id}`,
        { completed: 'yes' },
        { headers: token ? { Authorization: `Bearer ${token}` } : undefined }
      );
      setCompletionOpen(false);
      
      // Update the job in the cached state immediately
      setCachedByDate(prevCache => {
        const newCache = { ...prevCache };
        for (const [dateKey, jobs] of Object.entries(newCache)) {
          newCache[dateKey] = jobs.map(job => 
            job.id === selectedJob.id 
              ? { ...job, completed: 'yes' }
              : job
          );
        }
        return newCache;
      });
      
      // Check permissions before showing quote/invoice creation modals
      if (selectedJob.visitType === 'quote') {
        // Only show quote modal if user has quote write permission
        if (userPermissions?.quote_perm === 'write') {
          setQuoteOpen(true);
        }
      } else {
        // Only show invoice modal if user has invoice write permission
        if (userPermissions?.invoice_perm === 'write') {
          setInvoiceOpen(true);
        }
      }
    } catch (err) {
      console.error('Error updating job completion:', err);
      window.alert('Failed to update job completion. Please try again later.');
    }
  };

  const handleQuoteConfirm = () => {
    if (!selectedJob) return;
    setQuoteOpen(false);
    const q = new URLSearchParams({
      id: String(selectedJob.id),
      customerId: String(selectedJob.customerId ?? ''),
      customerName: selectedJob.customerName ?? '',
      title: selectedJob.title ?? '',
      description: selectedJob.description ?? '',
      price: String(selectedJob.price ?? ''),
    }).toString();
    navigate(`/create-quote?${q}`);
  };

  const handleQuoteCancel = async () => {
    setQuoteOpen(false);
    // No need to refresh jobs list since we already updated it
  };

  const handleInvoiceConfirm = () => {
    if (!selectedJob) return;
    setInvoiceOpen(false);
    const q = new URLSearchParams({
      customerName: selectedJob.customerName ?? '',
      title: selectedJob.title ?? '',
      description: selectedJob.description ?? '',
      price: String(selectedJob.price ?? ''),
      selectedDate: selectedJob.selectedDate ?? '',
      selectedTime: selectedJob.selectedTime ?? '',
      jobId: String(selectedJob.id),
    }).toString();
    navigate(`/create-invoice?${q}`);
  };

  const handleInvoiceCancel = async () => {
    setInvoiceOpen(false);
    // No need to refresh jobs list since we already updated it
  };

  const undoComplete = async (job) => {
    try {
      const token = getToken();
      await axios.put(
        `${API_BASE_URL}/api/jobs/edit/${job.id}`,
        { completed: 'no' },
        { headers: token ? { Authorization: `Bearer ${token}` } : undefined }
      );
      
      // Update the job in the cached state immediately
      setCachedByDate(prevCache => {
        const newCache = { ...prevCache };
        for (const [dateKey, jobs] of Object.entries(newCache)) {
          newCache[dateKey] = jobs.map(existingJob => 
            existingJob.id === job.id 
              ? { ...existingJob, completed: 'no' }
              : existingJob
          );
        }
        return newCache;
      });
    } catch (e) {
      console.error('Error undoing job completion', e);
      window.alert('Failed to undo job completion. Please try again later.');
    }
  };

  // ---------- RIGHT: New Job / Quote (lazy customers) ----------
  const [visitType, setVisitType] = useState(''); // 'job' | 'quote'
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [price, setPrice] = useState('');

  const [selectedDate, setSelectedDate] = useState(() => {
    const d = new Date();
    d.setSeconds(0, 0);
    return d;
  });

  const [customerName, setCustomerName] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [customers, setCustomers] = useState([]);
  const [customersLoaded, setCustomersLoaded] = useState(false);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [isNotesVisible, setIsNotesVisible] = useState(false);
  const [notes, setNotes] = useState('');
  const [users, setUsers] = useState([]);
  const [subscription, setSubscription] = useState('');
  const [assignedUserIds, setAssignedUserIds] = useState(['all']);
  const [isUserPickerVisible, setUserPickerVisible] = useState(false);

  const loadCustomersIfNeeded = useCallback(async () => {
    if (customersLoaded || loadingCustomers) return;
    try {
      setLoadingCustomers(true);
      const token = getToken();
      const res = await axios.get(`${API_BASE_URL}/api/customers`, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
      const list = coerceJobs(res.data); // coerce to array if backend wraps
      setCustomers(Array.isArray(list) ? list : []);
      setCustomersLoaded(true);
    } catch (err) {
      console.error('Error loading customers for job form:', err);
      window.alert('Failed to load customers for suggestions.');
    } finally {
      setLoadingCustomers(false);
    }
  }, [customersLoaded, loadingCustomers]);

  // Fetch users and subscription on component mount
  useEffect(() => {
    const fetchUsersAndSubscription = async () => {
      try {
        const sub = localStorage.getItem('subscription');
        setSubscription(sub);

        // Only fetch users if subscription is 'free' or 'pro'
        if (sub === 'free' || sub === 'pro') {
          const token = getToken();
          const response = await axios.get(`${API_BASE_URL}/api/getAllUsers`, {
            headers: token ? { Authorization: `Bearer ${token}` } : undefined,
          });
          setUsers(response.data);

          // Set default to 'All' users
          setAssignedUserIds(['all']);
        }
      } catch (error) {
        console.error('Error fetching users:', error);
      }
    };

    fetchUsersAndSubscription();
  }, []);

  // filter suggestions (limit to 30 to keep DOM light)
  useEffect(() => {
    if (!searchQuery.trim() || customers.length === 0) {
      setFilteredCustomers([]);
      return;
    }
    const q = searchQuery.toLowerCase();
    const matches = customers
      .filter((c) => (c?.name ?? '').toLowerCase().includes(q))
      .slice(0, 30);
    setFilteredCustomers(matches);
  }, [searchQuery, customers]);

  const validateAndSaveJob = async () => {
    if (!visitType) {
      window.alert('Please select Job or Quote.');
      return;
    }
    if (!title.trim()) {
      window.alert('Please enter a title.');
      return;
    }
    if (!customerName.trim()) {
      window.alert('Please choose a customer.');
      return;
    }

    try {
      const token = getToken();
      
      // Handle price: if empty or invalid, default to '0.00'
      let finalPrice = price.trim();
      if (finalPrice === '' || isNaN(parseFloat(finalPrice))) {
        finalPrice = '0.00';
      }
      
      const adjustedPrice = visitType === 'quote' ? '0.00' : finalPrice;

      // Determine assigned_type and assignedUserIds based on selection
      let assigned_type = null;
      let userIdsToSend = [];

      if (assignedUserIds.includes('all')) {
        // All users selected - send 'all' type with empty array
        assigned_type = 'all';
        userIdsToSend = [];
      } else {
        // Specific users selected - send 'specific' type with user IDs
        assigned_type = 'specific';
        userIdsToSend = assignedUserIds.map((id) => parseInt(id));
      }

      const payload = {
        customerName,
        title: title.trim(),
        description: description.trim() || '', // Save as empty string if blank
        price: adjustedPrice,
        date: selectedDate.toISOString().split('T')[0], // Format as UTC YYYY-MM-DD
        time: selectedDate.toISOString().split('T')[1].slice(0, 8), // Format as UTC HH:mm:ss
        completed: 'no',
        visitType,
        notes: notes.trim() || '', // Save as empty string if blank
        assigned_type: assigned_type,
        assignedUserIds: userIdsToSend,
      };

      await axios.post(`${API_BASE_URL}/api/jobs`, payload, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });

      // Clear right panel + refresh current visible range to reflect the new job
      setTitle('');
      setDescription('');
      setPrice('');
      setVisitType('');
      setCustomerName('');
      setSearchQuery('');
      setNotes('');
      setAssignedUserIds(['all']); // Reset to default
      // refetch just the week range around the selected date for freshness
      const startStr = format(subDays(currentWeekStart, 1), 'yyyy-MM-dd');
      const endStr = format(addDays(currentWeekStart, 8), 'yyyy-MM-dd');
      await fetchJobsForRange(startStr, endStr);
      navigate('/schedule', { replace: true });
    } catch (error) {
      console.error('Error saving job:', error);
      window.alert('Failed to save job.');
    }
  };

  const toggleUserPicker = () => {
    setUserPickerVisible(!isUserPickerVisible);
  };

  const toggleUserSelection = (userId) => {
    if (userId === 'all') {
      // If clicking 'All', select only 'all'
      setAssignedUserIds(['all']);
    } else {
      // If clicking a specific user, remove 'all' from selection
      const currentIds = assignedUserIds.filter(id => id !== 'all');
      
      if (currentIds.includes(userId)) {
        const newIds = currentIds.filter((id) => id !== userId);
        // If no users left, default back to 'all'
        setAssignedUserIds(newIds.length > 0 ? newIds : ['all']);
      } else {
        setAssignedUserIds([...currentIds, userId]);
      }
    }
  };

  // for sticky right panel size calculations
  const rightRef = useRef(null);

  // Reset all state when returning to the schedule page
  useEffect(() => {
    // Clear all cached data
    setCachedByDate({});
    setFetchedRanges([]);
    setCurrentWeekStart(startOfWeek(new Date(), { weekStartsOn: 0 }));
    
    // Clear form states
    setTitle('');
    setDescription('');
    setPrice('');
    setVisitType('');
    setCustomerName('');
    setSearchQuery('');
    setNotes('');
    setAssignedUserIds(['all']);
    setIsNotesVisible(false);
    
    // Force reload of jobs
    const startStr = format(subDays(startOfWeek(new Date(), { weekStartsOn: 0 }), 15), 'yyyy-MM-dd');
    const endStr = format(addDays(startOfWeek(new Date(), { weekStartsOn: 0 }), 21), 'yyyy-MM-dd');
    fetchJobsForRange(startStr, endStr);
    setFetchedRanges([{ start: startStr, end: endStr }]);
    loadCustomersForEmails();
  }, [location.pathname]); // Trigger when pathname changes (navigating back to schedule)

  return (
    <View style={styles.page}>
      {/* --- LEFT: Week schedule --- */}
      <View style={styles.leftCol}>
        {/* Week header */}
        <View style={styles.weekHeader}>
          <TouchableOpacity onPress={() => handleWeekChange('backward')}>
            <IoChevronBack size={24} color="black" />
          </TouchableOpacity>
          <Text style={styles.weekText}>
            {weekStartFormatted} - {weekEndFormatted}
          </Text>
          <TouchableOpacity onPress={() => handleWeekChange('forward')}>
            <IoChevronForward size={24} color="black" />
          </TouchableOpacity>
        </View>

        {/* Calendar view button */}
        <TouchableOpacity
          style={styles.calendarBtn}
          onPress={() => navigate('/calendar')}
        >
          <IoCalendar size={16} color="#fff" style={{ marginRight: 6 }} />
          <Text style={styles.calendarBtnText}>Calendar View</Text>
        </TouchableOpacity>

        <ScrollView contentContainerStyle={{ paddingBottom: 80 }}>
          {loadingJobs && (
            <Text style={{ textAlign: 'center', color: '#666', marginBottom: 8 }}>
              Loading jobs…
            </Text>
          )}

          {weekDays.map((day, idx) => {
            const dayJobs = jobsForDay(day);
            return (
              <View key={idx} style={styles.dayContainer}>
                <Text style={styles.dayHeader}>{format(day, 'EEEE, MMM d')}</Text>

                {dayJobs.length > 0 ? (
                  dayJobs.map((job) => {
                    const t = formatTimeLocal(job.selectedDate, job.selectedTime);
                    return (
                      <TouchableOpacity
                        key={job.id}
                        style={styles.card}
                        onPress={() => navigate(`/job-details?id=${encodeURIComponent(job.id)}`)}
                      >
                        <Text
                          style={[
                            styles.topLeftLabel,
                            {
                              backgroundColor: job.visitType === 'job' ? 'green' : 'lightblue',
                              color: job.visitType === 'job' ? 'white' : 'black',
                            },
                          ]}
                        >
                          {job.visitType === 'job' ? 'Job' : 'Quote'}
                        </Text>

                        <View style={[styles.cardHeader, { marginTop: 20 }]}>
                          <Text style={styles.jobTitle}>{job.title}</Text>
                          <View style={styles.timeContainer}>
                            <IoTime size={16} color="green" />
                            <Text style={styles.timeText}>{t}</Text>
                          </View>
                        </View>

                        <Text style={styles.jobDetail}>{`Description: ${job.description || 'No Description'}`}</Text>
                        <Text style={styles.jobDetail}>{`Customer: ${job.customerName}`}</Text>

                        <View style={styles.addressRow}>
                          <Text style={styles.jobDetail}>{job.address || 'No Address'}</Text>
                          <TouchableOpacity
                            style={styles.navigateButton}
                            onPress={() => {
                              const address = `${job.address}, ${job.city}, ${job.state}, ${job.zip}`;
                              const url = `https://maps.google.com/?q=${encodeURIComponent(address)}`;
                              window.open(url, '_blank', 'noopener,noreferrer');
                            }}
                          >
                            <View style={styles.iconTextContainer}>
                              <IoNavigate size={16} color="#007AFF" />
                              <Text style={styles.navigateText}>Map</Text>
                            </View>
                          </TouchableOpacity>
                        </View>

                        <Text style={styles.jobDetail}>{job.city || 'No City'}, {job.state || 'No State'} {job.zip || 'No Zip'}</Text>

                        {canSeePrices() && <Text style={styles.jobDetail}>Price: ${job.price}</Text>}
                        <Text style={styles.jobDetail}>{`Assigned To: ${job.assignedUserNames || 'Not assigned'}`}</Text>
                        
                        {/* Bottom row - layout changes based on whether email exists */}
                        {getCustomerEmail(job.customerName) ? (
                          // When email exists: Email on left, Complete/Undo on right (same row)
                          <View style={styles.bottomRowWithEmail}>
                            <TouchableOpacity
                              onPress={(e) => {
                                e.stopPropagation();
                                emailCustomer(getCustomerEmail(job.customerName));
                              }}
                              style={styles.iconButtonGreen}
                            >
                              <MdEmail size={20} color="white" />
                            </TouchableOpacity>
                            
                            {job.completed === 'no' ? (
                              <TouchableOpacity
                                style={[
                                  styles.completeButton,
                                  !canCompleteUndoJob(job) && styles.disabledButton
                                ]}
                                onPress={(e) => {
                                  e.stopPropagation();
                                  canCompleteUndoJob(job) && handleCompletePress(job);
                                }}
                                disabled={!canCompleteUndoJob(job)}
                              >
                                <Text style={styles.completeButtonText}>
                                  {canCompleteUndoJob(job) ? '✔ Complete' : 'Incomplete'}
                                </Text>
                              </TouchableOpacity>
                            ) : (
                              <View style={styles.completedContainer}>
                                <Text style={styles.completedText}>✔ Completed</Text>
                                {canCompleteUndoJob(job) && (
                                  <TouchableOpacity onPress={(e) => {
                                    e.stopPropagation();
                                    undoComplete(job);
                                  }}>
                                    <Text style={styles.undoText}>Undo</Text>
                                  </TouchableOpacity>
                                )}
                              </View>
                            )}
                          </View>
                        ) : (
                          // When NO email: Complete/Undo on separate row (right-aligned)
                          <View style={styles.completeButtonRow}>
                            {job.completed === 'no' ? (
                              <TouchableOpacity
                                style={[
                                  styles.completeButton,
                                  !canCompleteUndoJob(job) && styles.disabledButton
                                ]}
                                onPress={(e) => {
                                  e.stopPropagation();
                                  canCompleteUndoJob(job) && handleCompletePress(job);
                                }}
                                disabled={!canCompleteUndoJob(job)}
                              >
                                <Text style={styles.completeButtonText}>
                                  {canCompleteUndoJob(job) ? '✔ Complete' : 'Incomplete'}
                                </Text>
                              </TouchableOpacity>
                            ) : (
                              <View style={styles.completedContainer}>
                                <Text style={styles.completedText}>✔ Completed</Text>
                                {canCompleteUndoJob(job) && (
                                  <TouchableOpacity onPress={(e) => {
                                    e.stopPropagation();
                                    undoComplete(job);
                                  }}>
                                    <Text style={styles.undoText}>Undo</Text>
                                  </TouchableOpacity>
                                )}
                              </View>
                            )}
                          </View>
                        )}
                      </TouchableOpacity>
                    );
                  })
                ) : (
                  <Text style={styles.noJobs}>No jobs</Text>
                )}
              </View>
            );
          })}
        </ScrollView>
      </View>

      {/* --- RIGHT: New Job / Quote form (lazy customers) --- */}
      {!hasJobWritePermission() && (
                  <Text style={styles.readOnlyText}>You have read-only access to jobs.</Text>
      )}
      {!isMobile && hasJobWritePermission() && (
        <View ref={rightRef} style={styles.rightCol}>
          <Text style={styles.formHeader}>Schedule New Visit</Text>

          <Text style={styles.label}>Select Visit Type</Text>
          <View style={styles.selectionContainer}>
            <TouchableOpacity
              style={[styles.selectionButton, visitType === 'job' && styles.selectedButton]}
              onPress={() => setVisitType('job')}
            >
              <IoBriefcase size={18} color={visitType === 'job' ? '#fff' : '#000'} />
              <Text
                style={[
                  styles.selectionButtonText,
                  visitType === 'job' && styles.selectedButtonText,
                ]}
              >
                Job
              </Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.selectionButton, visitType === 'quote' && styles.selectedButton]}
              onPress={() => setVisitType('quote')}
            >
              <IoDocumentText size={18} color={visitType === 'quote' ? '#fff' : '#000'} />
              <Text
                style={[
                  styles.selectionButtonText,
                  visitType === 'quote' && styles.selectedButtonText,
                ]}
              >
                Quote
              </Text>
            </TouchableOpacity>
          </View>

          <Text style={styles.label}>Customer</Text>
          <TextInput
            style={styles.input}
            placeholder="Search customer..."
            value={searchQuery}
            onFocus={loadCustomersIfNeeded}
            onChangeText={(t) => {
              if (!customersLoaded) loadCustomersIfNeeded();
              setSearchQuery(t);
              setCustomerName(t);
            }}
          />
          {loadingCustomers && (
            <Text style={{ marginTop: 6, color: '#666' }}>Loading customers…</Text>
          )}
          {!!filteredCustomers.length && (
            <View style={styles.searchResults}>
              {filteredCustomers.map((c, idx) => (
                <TouchableOpacity
                  key={`${c.id || c.name}-${idx}`}
                  onPress={() => {
                    setCustomerName(c.name);
                    setSearchQuery(c.name);
                    setFilteredCustomers([]);
                  }}
                >
                  <Text style={styles.searchResult}>{c.name}</Text>
                </TouchableOpacity>
              ))}
            </View>
          )}

          <Text style={styles.label}>Title</Text>
          <TextInput style={styles.input} value={title} onChangeText={setTitle} />

          <Text style={styles.label}>Description</Text>
          <TextInput
            style={[styles.input, styles.textArea]}
            value={description}
            onChangeText={setDescription}
            multiline
          />

          {visitType === 'job' && (
            <>
              <Text style={styles.label}>Price</Text>
              <TextInput
                style={styles.input}
                value={price}
                onChangeText={setPrice}
                keyboardType="numeric"
              />
            </>
          )}

          <Text style={styles.label}>Date & Time</Text>
          <View style={styles.dateTimeRow}>
            <Text style={styles.dateTimePreview}>
              {format(selectedDate, 'MMMM dd, yyyy hh:mm a')}
            </Text>
            <input
              type="datetime-local"
              value={toLocalDatetimeInput(selectedDate)}
              onChange={(e) => {
                const next = new Date(e.target.value);
                if (!isNaN(next)) {
                  next.setSeconds(0, 0);
                  setSelectedDate(next);
                }
              }}
              style={styles.webDateTimeInput}
            />
          </View>

          {(subscription === 'free' || subscription === 'pro') && (
            <>
              <Text style={styles.label}>Assign to:</Text>
              <TouchableOpacity onPress={toggleUserPicker} style={styles.assignedUsersContainer}>
                <Text style={styles.assignedUsersText}>
                  {assignedUserIds.includes('all')
                    ? 'All'
                    : users
                        .filter((u) => assignedUserIds.includes(u.id.toString()))
                        .map((u) => `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email)
                        .join(', ')}
                </Text>
              </TouchableOpacity>
            </>
          )}
        

          {!isNotesVisible ? (
            <TouchableOpacity
              style={styles.addNotesButton}
              onPress={() => setIsNotesVisible(true)}
            >
              <MdAdd size={20} color="green" />
              <Text style={styles.addNotesText}>Add Notes</Text>
            </TouchableOpacity>
          ) : (
            <>
              <TouchableOpacity
                style={styles.hideNotesButton}
                onPress={() => setIsNotesVisible(false)}
              >
                <MdRemove size={20} color="red" />
                <Text style={styles.hideNotesText}>Hide Notes</Text>
              </TouchableOpacity>
              <View style={styles.fieldContainer}>
                <Text style={styles.label}>Notes (for internal use only):</Text>
                <TextInput
                  style={[styles.input, styles.textArea]}
                  placeholder="Enter notes here..."
                  multiline
                  numberOfLines={6}
                  onChangeText={setNotes}
                  value={notes}
                />
              </View>
            </>
          )}

          <TouchableOpacity style={styles.saveButton} onPress={validateAndSaveJob}>
            <Text style={styles.saveButtonText}>Save to Schedule</Text>
          </TouchableOpacity>
        </View>
      )}

      {/* Completion Modal */}
      {completionOpen && (
        <View style={styles.overlay}>
          <View style={styles.overlayCard}>
            <Text style={styles.modalTitle}>Complete Job</Text>
            <Text style={styles.modalMessage}>Has this visit been completed?</Text>
            <View style={styles.modalButtons}>
              <TouchableOpacity style={styles.modalButton} onPress={handleCompletionConfirm}>
                <Text style={styles.modalButtonText}>Yes</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.modalButton} onPress={() => setCompletionOpen(false)}>
                <Text style={styles.modalButtonText}>No</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      )}

      {/* Invoice Modal */}
      {invoiceOpen && (
        <View style={styles.overlay}>
          <View style={styles.overlayCard}>
            <Text style={styles.modalTitle}>Create Invoice</Text>
            <Text style={styles.modalMessage}>Would you like to create an invoice now?</Text>
            <View style={styles.modalButtons}>
              <TouchableOpacity style={styles.modalButton} onPress={handleInvoiceConfirm}>
                <Text style={styles.modalButtonText}>Yes</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.modalButton} onPress={handleInvoiceCancel}>
                <Text style={styles.modalButtonText}>No</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      )}

      {/* Quote Modal */}
      {quoteOpen && (
        <View style={styles.overlay}>
          <View style={styles.overlayCard}>
            <Text style={styles.modalTitle}>Create Quote</Text>
            <Text style={styles.modalMessage}>Would you like to create a quote to send to the customer?</Text>
            <View style={styles.modalButtons}>
              <TouchableOpacity style={styles.modalButton} onPress={handleQuoteConfirm}>
                <Text style={styles.modalButtonText}>Yes</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.modalButton} onPress={handleQuoteCancel}>
                <Text style={styles.modalButtonText}>No</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      )}

      {/* User Picker Modal */}
      {isUserPickerVisible && (
        <View style={styles.overlay}>
          <View style={styles.userPickerCard}>
            <View style={styles.userPickerHeader}>
              <Text style={styles.modalTitle}>Select Users</Text>
              <TouchableOpacity onPress={toggleUserPicker} style={styles.closeButton}>
                <Text style={styles.closeButtonText}>✕</Text>
              </TouchableOpacity>
            </View>
            <ScrollView style={styles.userListScroll}>
              {/* All option */}
              <TouchableOpacity
                style={styles.userOption}
                onPress={() => toggleUserSelection('all')}
              >
                <Text style={styles.userOptionText}>All</Text>
                {assignedUserIds.includes('all') && (
                  <Text style={styles.checkMark}>✓</Text>
                )}
              </TouchableOpacity>
              
              {/* Individual users */}
              {users.map((user) => {
                const fullName = `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email;
                const isSelected = assignedUserIds.includes(user.id.toString());
                return (
                  <TouchableOpacity
                    key={user.id}
                    style={styles.userOption}
                    onPress={() => toggleUserSelection(user.id.toString())}
                  >
                    <Text style={styles.userOptionText}>{fullName}</Text>
                    {isSelected && (
                      <Text style={styles.checkMark}>✓</Text>
                    )}
                  </TouchableOpacity>
                );
              })}
            </ScrollView>
            <TouchableOpacity
              style={styles.modalButton}
              onPress={toggleUserPicker}
            >
              <Text style={styles.modalButtonText}>Done</Text>
            </TouchableOpacity>
          </View>
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  page: {
    minHeight: '100vh',
    backgroundColor: '#fff',
    flexDirection: 'row',
    gap: 16,
    padding: 16,
  },

  // Columns
  leftCol: {
    flex: 1,
    minWidth: 0, // fixes flexbox overflow on web
  },
  rightCol: {
    width: 420,
    maxWidth: '40vw',
    backgroundColor: '#fafafa',
    borderWidth: 1,
    borderColor: '#e5e7eb',
    borderRadius: 10,
    padding: 16,
    position: 'sticky',
    top: 16,
    alignSelf: 'flex-start',
  },

  // Left: week header + list
  weekHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 12,
    backgroundColor: '#f0f8ff',
    borderRadius: 6,
  },
  weekText: { fontSize: 16, fontWeight: 'bold' },

  calendarBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'center',
    marginTop: 10,
    marginBottom: 10,
    backgroundColor: '#007AFF',
    paddingVertical: 6,
    paddingHorizontal: 12,
    borderRadius: 6,
  },
  calendarBtnText: { color: '#fff', fontSize: 14, fontWeight: 'bold' },

  dayContainer: { marginBottom: 20, paddingHorizontal: 4 },
  dayHeader: {
    backgroundColor: '#e0f0ff',
    padding: 10,
    fontSize: 16,
    fontWeight: 'bold',
    borderRadius: 4,
    marginBottom: 10,
    color: '#000',
  },

  card: {
    backgroundColor: '#f0f0f0',
    padding: 15,
    borderRadius: 8,
    marginBottom: 10,
    boxShadow: '0 1px 4px rgba(0,0,0,0.15)',
    position: 'relative',
  },
  topLeftLabel: {
    position: 'absolute',
    top: 10,
    left: 10,
    backgroundColor: 'lightblue',
    paddingLeft: 6,
    paddingRight: 6,
    paddingTop: 2,
    paddingBottom: 2,
    borderRadius: 4,
    fontWeight: 'bold',
    color: 'black',
  },
  cardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 10,
  },
  jobTitle: {
    fontWeight: 'bold',
    fontSize: 18,
    marginBottom: 4,
    textDecorationLine: 'underline',
  },
  timeContainer: { flexDirection: 'row', alignItems: 'center' },
  timeText: { fontSize: 14, marginLeft: 6, color: 'green' },

  jobDetail: { fontSize: 14, color: '#444', marginBottom: 4 },

  addressRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 4,
    gap: 8,
  },
  navigateButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'transparent',
    paddingVertical: 4,
    paddingHorizontal: 8,
    borderRadius: 4,
  },
  iconTextContainer: { flexDirection: 'row', alignItems: 'center' },
  navigateText: { color: '#007AFF', fontWeight: 'bold', marginLeft: 4 },

  noJobs: { fontStyle: 'italic', color: '#999', padding: 10 },

  emailButtonRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 10,
  },

  completeButtonRow: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    marginTop: 10,
  },

  bottomRowWithEmail: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 10,
  },

  completeButton: {
    backgroundColor: '#28a745',
    padding: 8,
    borderRadius: 4,
  },
  disabledButton: {
    backgroundColor: '#cccccc',
    opacity: 0.6,
  },
  completeButtonText: { color: '#fff', fontWeight: 'bold', fontSize: 14 },

  completedContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  completedText: { color: '#28a745', fontWeight: 'bold' },
  undoText: {
    color: '#007AFF',
    fontWeight: 'bold',
    textDecorationLine: 'underline',
    cursor: 'pointer',
  },

  iconButtonGreen: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'green',
    paddingVertical: 6,
    paddingHorizontal: 8,
    borderRadius: 5,
    cursor: 'pointer',
  },

  // Right: form
  formHeader: { fontSize: 18, fontWeight: 'bold', marginBottom: 8 },
  label: { marginTop: 12, fontWeight: 'bold' },
  input: {
    borderWidth: 1,
    borderColor: '#ccc',
    padding: 8,
    borderRadius: 4,
    marginTop: 6,
    backgroundColor: '#fff',
  },
  textArea: {
    height: 120,
    textAlignVertical: 'top',
  },
  selectionContainer: {
    flexDirection: 'row',
    gap: 8,
    marginTop: 8,
  },
  selectionButton: {
    flex: 1,
    padding: 10,
    borderRadius: 6,
    alignItems: 'center',
    backgroundColor: '#f9f9f9',
    borderWidth: 1,
    borderColor: '#ccc',
    flexDirection: 'row',
    justifyContent: 'center',
    gap: 8,
  },
  selectedButton: {
    backgroundColor: '#007BFF',
    borderColor: '#007BFF',
  },
  selectionButtonText: {
    color: '#000',
    fontSize: 15,
  },
  selectedButtonText: {
    color: '#fff',
    fontSize: 15,
  },

  dateTimeRow: { marginTop: 6, gap: 8 },
  dateTimePreview: { fontSize: 14, color: '#333', marginBottom: 6 },
  webDateTimeInput: {
    padding: 10,
    borderWidth: 1,
    borderColor: '#ccc',
    borderRadius: 4,
    outline: 'none',
    width: '100%',
    maxWidth: '100%',
    boxSizing: 'border-box',
    overflow: 'hidden',
  },

  searchResults: {
    marginTop: 6,
    backgroundColor: '#f9f9f9',
    borderRadius: 4,
    padding: 10,
    maxHeight: 240,
    overflowY: 'auto',
  },
  searchResult: {
    paddingVertical: 6,
    fontSize: 14,
    borderBottomWidth: 1,
    borderBottomColor: '#eee',
  },

  addNotesButton: { flexDirection: 'row', alignItems: 'center', marginTop: 10, gap: 6 },
  addNotesText: { color: 'green' },
  hideNotesButton: { flexDirection: 'row', alignItems: 'center', marginTop: 10, gap: 6 },
  hideNotesText: { color: 'red' },
  fieldContainer: { marginTop: 0 },

  saveButton: {
    backgroundColor: '#007AFF',
    padding: 12,
    borderRadius: 6,
    alignItems: 'center',
    marginTop: 16,
  },
  saveButtonText: { color: '#fff', fontSize: 16, fontWeight: 'bold' },

  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
  },
  overlayCard: {
    backgroundColor: '#fff',
    padding: 20,
    borderRadius: 10,
    width: '80%',
    maxWidth: 400,
    alignItems: 'center',
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 10,
  },
  modalMessage: {
    fontSize: 16,
    marginBottom: 20,
    textAlign: 'center',
  },
  modalButtons: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    width: '100%',
  },
  modalButton: {
    backgroundColor: '#007AFF',
    padding: 10,
    borderRadius: 6,
    alignItems: 'center',
    flex: 1,
    marginHorizontal: 5,
  },
  modalButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: 'bold',
  },
  userPickerCard: {
    backgroundColor: '#fff',
    padding: 20,
    borderRadius: 10,
    width: '80%',
    maxWidth: 400,
    alignItems: 'center',
  },
  userPickerHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    marginBottom: 10,
  },
  closeButton: {
    padding: 10,
  },
  closeButtonText: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#007AFF',
  },
  userListScroll: {
    width: '100%',
    maxHeight: 300,
    marginBottom: 10,
  },
  userOption: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: '#eee',
  },
  userOptionText: {
    fontSize: 16,
  },
  checkMark: {
    fontSize: 16,
    color: '#007AFF',
  },
  assignedUsersContainer: {
    borderWidth: 1,
    borderColor: '#ccc',
    padding: 10,
    borderRadius: 4,
    marginTop: 6,
    backgroundColor: '#fff',
    cursor: 'pointer',
  },
  assignedUsersText: {
    fontSize: 16,
    color: '#333',
  },
  readOnlyText: { textAlign: 'center', fontSize: 16, color: '#777', marginTop: 20 },

});
